import { ButtonPrimary, ButtonTertiary } from "@nova/component-btn"
import { HighPriorityNovaModalContainer } from "@nova/component-nova-modal-container"
import { useUploadFlowContext } from "../useUploadFlowContext"
import { useCallback, useMemo, useState } from "react"
import { ID, UploadFlowWarnings } from "@nova/constants"
import classNames from "classnames"
import { Checkbox } from "@nova/component-checkbox"

export const UploadWarningModal = ({
    onHideModal,
    removeSelected,
    isRemoving,
    beforeUpload,
}: {
    onHideModal: () => void
    removeSelected: (uploadIds: Record<string, boolean>) => Promise<void>
    isRemoving: boolean
    beforeUpload?: () => Promise<void>
}) => {
    const { uploadFlowWarnings, flightId, uploadFlight, uploads } = useUploadFlowContext()

    const [keysToDelete, setKeysToDelete] = useState<Partial<Record<UploadFlowWarnings, boolean>>>(
        {}
    )
    const warnings = uploadFlowWarnings?.[flightId]

    const handleConfirm = useCallback(async () => {
        const idsToRemove: Record<string, boolean> = {}

        Object.entries(keysToDelete).forEach(([key, value]) => {
            if (!value || !warnings) return
            const ids = warnings[key as UploadFlowWarnings].ids
            ids.forEach((id) => (idsToRemove[id] = true))
        })

        await removeSelected(idsToRemove)
        await beforeUpload?.()
        uploadFlight(flightId)
        onHideModal()
    }, [flightId, warnings, keysToDelete, onHideModal, removeSelected, uploadFlight, beforeUpload])

    const totalCount = useMemo(() => {
        if (!warnings) return uploads.length
        const ids = new Set<ID>()
        Object.entries(warnings).forEach(([type, warning]) => {
            if (!keysToDelete[type as UploadFlowWarnings]) return
            warning.ids.forEach((id) => ids.add(id))
        })
        return uploads.length - ids.size
    }, [warnings, keysToDelete, uploads.length])

    if (!warnings) return null
    return (
        <HighPriorityNovaModalContainer
            title="Confirm Image Types"
            className="w-[22.5rem]"
            bottomBar={
                <div className="w-full flex justify-end items-center gap-2">
                    <ButtonTertiary title="Cancel" onClick={onHideModal} />
                    <ButtonPrimary
                        title={isRemoving ? "Loading..." : "Confirm"}
                        onClick={handleConfirm}
                        disabled={!totalCount}
                    />
                </div>
            }
            closeModal={onHideModal}
        >
            <h5 className="text-gray-0 mt-4 mb-4">{`You're adding a mixed set of images`}</h5>
            <div className="flex flex-col gap-2 mb-4">
                {Object.entries(warnings)
                    .reverse()
                    .map(([type, warning], idx) => {
                        const count = warning.ids.length
                        const isChecked =
                            keysToDelete[type as UploadFlowWarnings] === undefined ||
                            keysToDelete[type as UploadFlowWarnings] === false
                        if (!count) return
                        return (
                            <div
                                className="flex items-center justify-between cursor-pointer select-none"
                                key={idx}
                                onClick={() =>
                                    setKeysToDelete((prev) => ({
                                        ...prev,
                                        [type as UploadFlowWarnings]: isChecked,
                                    }))
                                }
                            >
                                <div className="flex gap-2 items-center">
                                    <Checkbox
                                        isChecked={isChecked}
                                        onClick={() =>
                                            setKeysToDelete((prev) => ({
                                                ...prev,
                                                [type as UploadFlowWarnings]: isChecked,
                                            }))
                                        }
                                    />
                                    <div
                                        className={classNames("h-2 w-2 rounded-[3px]", {
                                            "opacity-50": !isChecked,
                                        })}
                                        style={{ backgroundColor: warning.color }}
                                    />
                                    <h5
                                        className={classNames("text-gray-0", {
                                            "!text-gray-300": !isChecked,
                                        })}
                                    >
                                        {type}
                                    </h5>
                                </div>
                                <h5 className="text-gray-300">{count}</h5>
                            </div>
                        )
                    })}
            </div>
            <div className="border-b border-gray-700" />
            <div className="flex justify-between items-center my-2">
                <h5 className="text-gray-0">Total</h5>
                <h5 className="text-gray-0">{totalCount}</h5>
            </div>
            <div className="border-b border-gray-700" />
            <h5 className="mt-4 text-gray-300">
                This could cause errors during processing and inconsistent results.
            </h5>
            <h5 className="text-gray-0 mt-5">Proceed anyway?</h5>
        </HighPriorityNovaModalContainer>
    )
}
